import { orNA } from "@/utils/placeholder";

/**
 * The Webhooks tab's heading, and why Retry is off when it is.
 *
 * `configured` comes from GET /call/:id/webhook-logs, which reads the URL off the
 * call's agent — so a call whose agent was deleted (agent_id NULL) reads as
 * "not configured" there, although its deliveries did go out. Such a call keeps
 * the plain heading; only the resend is off, because no URL is left to send to.
 */
export const webhookStatus = (agentId: string | null | undefined, configured: boolean) => {
  if (!agentId) {
    return {
      heading: "Delivery attempts",
      retryOffBecause: `${orNA(null, "Agent")} (deleted) — there is no webhook URL to resend to`,
    };
  }
  return configured
    ? { heading: "Delivery attempts", retryOffBecause: undefined }
    : { heading: "Webhooks not enabled for this agent", retryOffBecause: "No webhook URL configured" };
};

/** POST /call/:id/webhooks/resend's answer: each resent event's result. */
export type ResendResults = Record<string, { reason?: string; status?: number | null }>;

/**
 * Results after which no attempt of that event comes on its own — given up,
 * held, or no URL — so a later read cannot show it landing.
 */
const FINAL_REASONS = ["no_webhook_url", "held", "dead"];

/**
 * The one toast a resend ends with. The worst result wins — no URL, held, given
 * up, failed, the call unreadable, already being sent — and only a resend where
 * none of those happened says it went out.
 */
export function resendToast(results: ResendResults): { tone: "success" | "info" | "warning" | "error"; message: string } {
  const values = Object.values(results);
  const find = (reason: string) => values.find((r) => r.reason === reason);
  if (find("no_webhook_url")) return { tone: "warning", message: "No webhook URL is configured for this agent" };
  if (find("held")) {
    // The contract refused the body: nothing was POSTed, and nothing retries it
    // until someone at Verbeo re-queues it (ops is alerted on held rows).
    return { tone: "error", message: "Not sent: this event's payload failed our validation check, so it is held for our team to review." };
  }
  // Given up: nothing retries it. A resend resets the attempt count and rebuilds
  // a purged body, so `dead` with no status is the body purged between the
  // re-open and the send — and the next resend rebuilds that one too.
  const dead = find("dead");
  if (dead) {
    return {
      tone: "error",
      message: dead.status
        ? `Webhook endpoint returned an error (${dead.status}) — see the log. It won't be retried automatically.`
        : "Not sent: this event's stored payload expired as it was being resent. Resend it again and it will be rebuilt from the call.",
    };
  }
  // Anything that fails stays queued and is retried automatically with backoff.
  const failed = find("failed");
  if (failed) {
    return {
      tone: "error",
      message: `Webhook endpoint returned an error${failed.status ? ` (${failed.status})` : ""} — see the log. We'll keep retrying automatically.`,
    };
  }
  // The call's row could not be read: nothing was POSTed, and the event is due again a minute on.
  if (find("row_not_found")) {
    return { tone: "error", message: "Not sent: this call could not be read just now. We'll keep retrying automatically." };
  }
  // Another sender holds the event: its POST is already on the wire.
  if (find("not_claimed")) {
    return { tone: "info", message: "Already being sent: another send of this event is in progress. The log will show how it went." };
  }
  return { tone: "success", message: "Webhook resend triggered" };
}

/** The resent events a later read can still show landing: all but those whose result is final. */
export function eventsStillDue(events: string[], results: ResendResults): string[] {
  const final = Object.entries(results)
    .filter(([, result]) => FINAL_REASONS.some((reason) => reason === result.reason))
    .map(([event]) => event);
  return events.filter((event) => !final.includes(event));
}
